<template>
    <div>
        <v-card class="mb-4">
            <v-card-title class="title-markazi">
                {{ $t('prompt.prompts') }}
                <v-text-field
                    v-model="searchFilter"
                    density="compact"
                    hide-details
                    class="ms-4"
                    style="max-width: 200px"
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    color="grey-darken-1"
                    bg-color="grey-lighten-4"
                    placeholder="Search"
                    @update:model-value="applyFilter"
                    clearable
                />

                <v-spacer />
                <v-btn color="primary" prepend-icon="mdi-chat-plus-outline" @click="chatDialog = true" class="me-2">
                    {{ $t('prompt.chat') }}
                </v-btn>
                <v-btn color="primary" prepend-icon="mdi-plus" @click="createPromptDialog = true">
                    {{ $t('prompt.addPrompt') }}
                </v-btn>
            </v-card-title>
        </v-card>

        <!-- Chat dialog -->
        <v-dialog v-model="chatDialog" max-width="1200px">
            <v-card>
                <v-card-title>{{ $t('prompt.chat') }}</v-card-title>
                <v-card-text style="min-height: 600px">
                    <v-form ref="chatForm" v-model="chatFormValid">
                        <v-row>
                            <v-col cols="3">
                                <v-select
                                    v-model="chatOptions.provider"
                                    :label="$t('prompt.provider')"
                                    :items="Object.keys(providerModels)"
                                    :rules="[v => !!v || $t('prompt.validation.providerRequired')]"
                                    required
                                    @update:model-value="chatOptions.model = ''"
                                />
                                <v-select
                                    v-model="chatOptions.model"
                                    :label="$t('prompt.model')"
                                    :items="providerModels[chatOptions.provider] || []"
                                    :rules="[v => !!v || $t('prompt.validation.modelRequired')]"
                                    required
                                    :disabled="!chatOptions.provider"
                                />
                                <v-slider
                                    v-model="chatOptions.temperature"
                                    :label="$t('prompt.temperature')"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    thumb-label
                                />
                                <v-select
                                    v-model="chatOptions.knowledges"
                                    :label="$t('prompt.knowledge')"
                                    :items="knowledges"
                                    item-title="title"
                                    item-value="id"
                                    multiple
                                    chips
                                    clearable
                                    :hint="$t('prompt.selectRag')"
                                    persistent-hint
                                />
                                <v-select
                                    v-model="chatOptions.tools"
                                    :label="$t('prompt.tools')"
                                    :items="tools"
                                    item-title="name"
                                    item-value="id"
                                    multiple
                                    chips
                                    clearable
                                    :hint="$t('prompt.selectAction')"
                                    persistent-hint
                                />
                            </v-col>
                            <v-col cols="9">
                                <v-textarea
                                    v-model="chatPrompt"
                                    :label="$t('prompt.content')"
                                    :rules="[
                                        v => !!v || $t('prompt.validation.contentRequired'),
                                        v => !v || v.length >= 10 || $t('prompt.validation.contentTooShort'),
                                        v => !v || v.length <= MAX_PROMPT_LENGTH || $t('prompt.validation.contentTooLong')
                                    ]"
                                    rows="4"
                                    auto-grow
                                    counter
                                    required
                                />
                                <v-textarea
                                    v-model="chatOptions.search"
                                    :label="$t('prompt.search')"
                                    rows="2"
                                    :disabled="!chatOptions.knowledges.length"
                                    :hint="!chatOptions.knowledges.length ? $t('prompt.searchRequiresKnowledge') : ''"
                                    persistent-hint
                                />
                                <v-btn
                                    color="primary"
                                    block
                                    class="mt-2 mb-4"
                                    @click="validateAndProcessChat"
                                    :loading="processingChat"
                                    :disabled="!isValidChatContent"
                                >
                                    {{ $t('prompt.chat') }}
                                </v-btn>
                                <div v-if="chatResponse" v-html="sanitizedChatResponse" :loading="processingChat" :class="[
                                    'response-textarea',
                                    'flex-grow-1',
                                    'pa-4',
                                    'bordered-div',
                                    theme.global.current.value.dark ? 'response-textarea-dark' : 'response-textarea-light'
                                ]" style="overflow-y: auto; min-height: 300px" />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" @click="chatDialog = false">
                        {{ $t('dialog.close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Create prompt dialog -->
        <v-dialog v-model="createPromptDialog" max-width="600px">
            <v-card>
                <v-card-title>{{ $t('prompt.createPrompt') }}</v-card-title>
                <v-card-text>
                    <v-form ref="form" @submit.prevent="savePrompt" v-model="formValid">
                        <v-text-field
                            v-model="newPrompt.title"
                            :label="$t('prompt.title')"
                            :rules="[v => !!v || $t('prompt.validation.titleRequired')]"
                            required
                        />
                        <v-textarea
                            v-model="newPrompt.content"
                            :label="$t('prompt.content')"
                            :rules="[
                                v => !!v || $t('prompt.validation.contentRequired'),
                                v => !v || v.length >= 10 || $t('prompt.validation.contentTooShort'),
                                v => !v || v.length <= MAX_PROMPT_LENGTH || $t('prompt.validation.contentTooLong')
                            ]"
                            required
                        />
                        <v-textarea
                            v-model="newPrompt.search"
                            :label="$t('prompt.search')"
                            rows="2"
                        />
                        <v-select
                            v-model="newPrompt.provider"
                            :label="$t('prompt.provider')"
                            :items="Object.keys(providerModels)"
                            :rules="[v => !!v || $t('prompt.validation.providerRequired')]"
                            required
                            @update:model-value="newPrompt.model = ''"
                        />
                        <v-select
                            v-model="newPrompt.model"
                            :label="$t('prompt.model')"
                            :items="availableModels"
                            :rules="[v => !!v || $t('prompt.validation.modelRequired')]"
                            required
                            :disabled="!newPrompt.provider"
                        />
                        <v-select
                            v-model="newPrompt.knowledges"
                            :label="$t('menu.knowledge')"
                            :items="knowledges"
                            item-title="title"
                            item-value="id"
                            multiple
                            chips
                            clearable
                            :hint="$t('prompt.selectRag')"
                            persistent-hint
                        />
                        <v-select
                            v-model="newPrompt.tools"
                            :label="$t('prompt.tools')"
                            :items="tools"
                            item-title="name"
                            item-value="id"
                            multiple
                            chips
                            clearable
                            :hint="$t('prompt.selectAction')"
                            persistent-hint
                        />
                        <v-slider
                            v-model="newPrompt.temperature"
                            :label="$t('prompt.temperature')"
                            min="0"
                            max="1"
                            step="0.1"
                            thumb-label
                        />
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey" @click="createPromptDialog = false">
                        {{ $t('dialog.cancel') }}
                    </v-btn>
                    <v-btn color="primary" @click="validateAndSave" :loading="loading">
                        {{ $t('dialog.save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Edit prompt dialog -->
        <v-dialog v-model="editDialog" max-width="600px">
            <v-card>
                <v-card-title>
                    {{ $t('prompt.editPrompt') }}
                </v-card-title>
                <v-card-text>
                    <v-form @submit.prevent="updatePrompt">
                        <v-text-field v-model="editedPrompt.title" :label="$t('prompt.title')" required />
                        <v-textarea v-model="editedPrompt.content" :label="$t('prompt.content')" required />
                        <v-textarea v-model="editedPrompt.search" :label="$t('prompt.search')" rows="2" />
                        <v-select v-model="editedPrompt.provider" :label="$t('prompt.provider')"
                            :items="Object.keys(providerModels)" required
                            @update:model-value="editedPrompt.model = ''" />
                        <v-select v-model="editedPrompt.model" :label="$t('prompt.model')" :items="availableModelsEdit"
                            required :disabled="!editedPrompt.provider" />
                        <v-select v-model="editedPrompt.knowledges" :label="$t('menu.knowledge')" :items="knowledges"
                            item-title="title" item-value="id" multiple chips clearable :hint="$t('prompt.selectRag')"
                            persistent-hint />
                        <v-select
                            v-model="editedPrompt.tools"
                            :label="$t('prompt.tools')"
                            :items="tools"
                            item-title="name"
                            item-value="id"
                            multiple
                            chips
                            clearable
                            :hint="$t('prompt.selectAction')"
                            persistent-hint
                        />
                        <v-slider v-model="editedPrompt.temperature" :label="$t('prompt.temperature')" min="0" max="1"
                            step="0.1" thumb-label />
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="secondary" @click="improvePrompt">
                        {{ $t('prompt.improvePrompt') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn color="justgray" @click="editDialog = false">
                        {{ $t('dialog.cancel') }}
                    </v-btn>
                    <v-btn color="violean" @click="updatePrompt" :loading="loading">
                        {{ $t('dialog.update') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- 'Improve my prompt' dialog -->
        <v-dialog v-model="improveDialog" max-width="1200px">
            <v-card>
                <v-card-title>{{ $t('prompt.improvePrompt') }}</v-card-title>
                <v-card-text>
                    <v-row>
                        <!-- Left column with prompt and analysis -->
                        <v-col cols="6" class="d-flex flex-column">
                            <!-- Current prompt section -->
                            <v-textarea v-model="editedPrompt.content" :label="$t('prompt.currentPrompt')" auto-grow
                                rows="6" variant="outlined" :counter="MAX_PROMPT_LENGTH" class="mb-3" />

                            <!-- Analyze and Process buttons -->
                            <div class="d-flex justify-center mb-4">
                                <v-btn color="primary" @click="improvePrompt" :loading="improvingPrompt"
                                    :disabled="improvingPrompt" class="me-2">
                                    {{ $t('prompt.analyze') || 'Analyze' }}
                                </v-btn>
                                <v-btn color="secondary" @click="processCurrentPrompt" :loading="processingCurrentPrompt"
                                    :disabled="processingCurrentPrompt">
                                    {{ $t('prompt.process') || 'Process' }}
                                </v-btn>
                            </div>

                            <!-- Display strengths and weaknesses -->
                            <div v-if="improvements" class="improvements-container mb-4">
                                <h4 class="mb-2">{{ $t('prompt.improvements') }}</h4>
                                <v-card class="pa-0" elevation="0" border>
                                    <template v-for="(section, sectionIndex) in [
                                        {
                                            items: improvements.good_things,
                                            title: $t('prompt.strengths'),
                                            icon: 'mdi-check-circle',
                                            color: 'green',
                                            textClass: 'text-green-darken-1'
                                        },
                                        {
                                            items: improvements.bad_things,
                                            title: $t('prompt.weaknesses'),
                                            icon: 'mdi-alert-circle',
                                            color: 'red',
                                            textClass: 'text-red-darken-1'
                                        },
                                        {
                                            title: 'Improved Version of the Prompt:',
                                            icon: 'mdi-file-document-edit',
                                            color: 'info',
                                            textClass: 'text-info',
                                            customContent: true
                                        },
                                        {
                                            title: 'Explanation of Improvements:',
                                            icon: 'mdi-lightbulb-on',
                                            color: 'primary',
                                            textClass: 'text-primary',
                                            customContent: true
                                        }
                                    ]" :key="sectionIndex">
                                        <div :class="[section.bgClass, 'mb-1 pa-4 rounded']">
                                            <div class="d-flex align-center">
                                                <v-icon :color="section.color" class="me-2">{{ section.icon }}</v-icon>
                                                <span :class="['text-' + section.color, 'text-h8', 'font-weight-bold']">{{ section.title }}</span>
                                            </div>

                                            <!-- Standard content for strengths/weaknesses -->
                                            <template v-if="!section.customContent">
                                                <div v-for="(item, index) in section.items"
                                                    :key="sectionIndex + '-' + index" class="ml-8 mt-2">
                                                    <div :class="[section.textClass, 'font-weight-bold']">{{ item.aspect }}</div>
                                                    <div class="text-body-2">{{ item.description }}</div>
                                                </div>
                                                <div v-if="!section.items || section.items.length === 0" class="ml-8 mt-2 font-italic text-body-2">
                                                    {{ section.title === $t('prompt.strengths') ?
                                                        "Unable to identify specific strengths in the prompt." :
                                                        "Unable to identify specific weaknesses in the prompt."
                                                    }}
                                                </div>
                                            </template>

                                            <!-- Custom content for improved prompt -->
                                            <template v-else-if="section.title === 'Improved Version of the Prompt:'">
                                                <div v-if="improvedPrompt" class="ml-8 mt-2">
                                                    <div class="text-body-2 pa-3" style="border-radius: 4px; border-left: 4px solid var(--v-info-base); white-space: pre-wrap; word-break: break-word; font-family: monospace; box-shadow: 0 2px 4px rgba(0,0,0,0.05);" v-html="sanitizedImprovedPrompt"></div>
                                                    <div class="d-flex justify-end mt-2">
                                                        <v-btn size="small" color="info" variant="text" density="compact"
                                                            @click="useImprovedPrompt"
                                                            prepend-icon="mdi-content-copy">
                                                            Use this prompt
                                                        </v-btn>
                                                        <v-btn size="small" color="primary" variant="text" density="compact"
                                                            @click="processWithImprovedPrompt"
                                                            :loading="processingImproved"
                                                            :disabled="processingImproved"
                                                            prepend-icon="mdi-play">
                                                            Process with this prompt
                                                        </v-btn>
                                                    </div>
                                                </div>
                                                <div v-else class="ml-8 mt-2 font-italic text-body-2">
                                                    No improved prompt suggestion available.
                                                </div>
                                            </template>

                                            <!-- Custom content for explanation -->
                                            <template v-else-if="section.title === 'Explanation of Improvements:'">
                                                <div v-if="improvementExplanation" class="ml-8 mt-2">
                                                    <div class="text-body-2 pa-3" style="white-space: pre-wrap; line-height: 1.5; border-radius: 4px; border-left: 4px solid var(--v-primary-base); box-shadow: 0 2px 4px rgba(0,0,0,0.05);" v-html="sanitizedImprovementExplanation"></div>
                                                </div>
                                                <div v-else class="ml-8 mt-2 font-italic text-body-2">
                                                    No explanation available.
                                                </div>
                                            </template>
                                        </div>
                                    </template>
                                </v-card>
                            </div>
                        </v-col>

                        <!-- Right column with results -->
                        <v-col cols="6" class="d-flex">
                            <div class="result-container d-flex flex-column" style="width: 100%; height: 100%;">
                                <h3 class="mb-2">{{ $t('prompt.processResult') || 'Processing Result' }}</h3>
                                <div v-if="currentAnswer" v-html="sanitizedCurrentAnswer" :class="[
                                    'response-textarea',
                                    'pa-4',
                                    'bordered-div',
                                    'flex-grow-1',
                                    theme.global.current.value.dark ? 'response-textarea-dark' : 'response-textarea-light'
                                ]" style="overflow-y: auto; min-height: 400px;"></div>
                                <div v-else :class="[
                                    'response-textarea',
                                    'pa-4',
                                    'bordered-div',
                                    'flex-grow-1',
                                    theme.global.current.value.dark ? 'response-textarea-dark' : 'response-textarea-light'
                                ]" style="overflow-y: auto; min-height: 400px;">
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="justgray" @click="improveDialog = false">
                        {{ $t('dialog.close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Prompts table -->
        <v-data-table :headers="headers"
        :items="filteredPrompts"
        :loading="isLoading"
        :sort-by="[{ key: 'updatedAt', order: 'desc' }]"
        class="pt-sans-table uppercase-headers" density="comfortable"
        :items-per-page="promptItemsPerPage"
        @update:items-per-page="updatePromptItemsPerPage">
                        <template v-slot:item.status="{ item }">
                <div @click="copyPromptId(item)" style="cursor: pointer">
                    <v-tooltip :text="item.id" location="end">
                        <template v-slot:activator="{ props }">
                            <span v-bind="props">{{ item.title }}</span>
                        </template>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:item.content="{ item }">
                {{ truncateContent(item.content) }}
            </template>
            <template v-slot:item.rag="{ item }">
                <v-tooltip
                    :text="$t('prompt.usesRag')"
                    location="end">
                    <template v-slot:activator="{ props }">
                        <v-chip v-if="item.knowledges?.length > 0" v-bind="props" color="success" size="small">
                            <v-icon size="small">
                                mdi-file-document-outline
                            </v-icon>
                        </v-chip>
                    </template>
                </v-tooltip>
            </template>
            <template v-slot:item.actions="{ item }">
                <div class="d-flex align-center justify-end no-wrap" style="min-width: 100px">
                    <v-tooltip :text="$t('prompt.processPrompt')" location="end">
                        <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="large" color="apple" class="me-2" @click="processPrompt(item)">
                                mdi-play
                            </v-icon>
                        </template>
                    </v-tooltip>

                    <v-tooltip :text="$t('prompt.editPrompt')" location="end">
                        <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" color="#9D9D9F" class="me-2" @click="editPrompt(item)">
                                mdi-pencil
                            </v-icon>
                        </template>
                    </v-tooltip>

                    <v-tooltip :text="$t('prompt.deletePrompt')" location="end">
                        <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" color="error" @click="deletePrompt(item)">
                                mdi-delete
                            </v-icon>
                        </template>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:item.provider="{ item }">
                <div class="d-flex align-center">
                    <img :src="`${item.provider}.png`" :alt="item.provider" class="me-2" width="20" height="20"
                        rounded="xl" />
                    {{ item.provider }}
                </div>
            </template>
        </v-data-table>

        <!-- Prompt response dialog -->
        <v-dialog v-model="responseDialog" max-width="800px" transition="dialog-bottom-transition">
            <v-card class="resizable">
                <v-card-title>
                    {{ $t('prompt.promptResponse') }}
                </v-card-title>
                <v-card-text>
                    <div v-html="sanitizedMarkdown" :class="[
                        'response-textarea',
                        'pa-4',
                        'bordered-div',
                        theme.global.current.value.dark ? 'response-textarea-dark' : 'response-textarea-light'
                    ]" style="min-height: 300px; overflow-y: auto" />
                </v-card-text>
                <v-card-actions>
                    <v-tooltip :text="$t('dialog.copyToClipboard')" location="start">
                        <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" @click="copyToClipboard(promptResponse)" color="primary"
                                class="cursor-pointer">
                                mdi-content-copy
                            </v-icon>
                        </template>
                    </v-tooltip>
                    <v-spacer />
                    <v-btn color="primary" @click="responseDialog = false">
                        {{ $t('dialog.close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { apiClient } from '@/services/api';

const truncateContent = (content, maxLength = 500) => {
    if (!content) return '';
    return content.length > maxLength ? `${content.slice(0, maxLength)}...` : content;
};
import { useI18n } from 'vue-i18n';
import { useSnackbarStore } from '@/stores/snackbarStore'
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useTheme } from 'vuetify'
import { userStore } from '@/stores/userStore'
import { storeToRefs } from 'pinia'
import { Logger } from '@azure/msal-browser';

// Use local state for the prompt pagination, completely isolated from other views
const promptItemsPerPage = ref(userStore().getViewItemsPerPage('prompt'))

// Function to update prompt-specific pagination
const updatePromptItemsPerPage = (itemsPerPage) => {
    promptItemsPerPage.value = itemsPerPage
    userStore().setViewItemsPerPage('prompt', itemsPerPage)
}

/* import { PublicClientApplication } from '@azure/msal-browser' */
// import { msalConfig, silentLogin } from '../services/entraID'

/* const { firstname } = storeToRefs(userStore()) */


const MAX_PROMPT_LENGTH = 30000;
const defaultTemperature = 0.1;

const prompts = ref([]);
const loading = ref(true);
const searchFilter = ref('');
const providerFilter = ref('');
const modelFilter = ref('');
const dialog = ref(false);
const editDialog = ref(false);
const improveDialog = ref(false);
const createPromptDialog = ref(false);
const currentAnswer = ref('');
const improvingPrompt = ref(false);
const processingOnly = ref(false);
const newPrompt = ref({
    userId: "1",
    title: '',
    content: '',
    search: '',
    provider: '',
    model: '',
    temperature: defaultTemperature,
    knowledges: [],
    tools: []
});
const editedPrompt = ref({
    id: '',
    userId: "1",
    title: '',
    content: '',
    search: '',
    provider: '',
    model: '',
    temperature: defaultTemperature,
    knowledges: [],
    tools: []
});
const responseDialog = ref(false);
const promptResponse = ref('');
const snackbarStore = useSnackbarStore()
const knowledges = ref([]);
const tools = ref([]);
const { t, locale } = useI18n()
const theme = useTheme()
const chatDialog = ref(false);
const chatPrompt = ref('');
const chatResponse = ref('');
const processingChat = ref(false);
const chatOptions = ref({
    provider: 'openai',
    model: 'gpt-4-turbo',
    temperature: defaultTemperature,
    search: '',
    knowledges: [],
    tools: []
});
const headers = computed(() => [
    { title: t('prompt.title'), key: 'title', align: 'start' },
    { title: t('prompt.content'), key: 'content' },
    { title: t('prompt.provider'), key: 'provider' },
    { title: t('prompt.model'), key: 'model' },
    { title: t('prompt.temperature'), key: 'temperature' },
    { title: '', key: 'rag', width: '48px' },
    { title: '', key: 'actions', sortable: false, align: 'end', width: '120px' }
]);

const filteredPrompts = computed(() => {
    if (!searchFilter.value) return prompts.value;

    const searchTerm = searchFilter.value.toLowerCase();
    return prompts.value.filter(prompt =>
        prompt.title.toLowerCase().includes(searchTerm) ||
        prompt.content.toLowerCase().includes(searchTerm)
    );
});

const providerModels = {
    'openai': ['gpt-4o-2024-11-20', 'gpt-4o-mini', 'o1', 'o1-preview', 'o1-mini', 'o3-mini', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo'],
    'anthropic': ['claude-sonnet-4-5', 'claude-haiku-4-5', 'claude-opus-4-1'],
    'mistral': ['mistral-large-latest', 'pixtral-large-latest', 'ministral-3b-latest', 'ministral-8b-latest', 'mistral-small-latest', 'codestral-latest', 'mistral-saba-latest'],
    'google': ['gemini-2.0-pro-exp-02-05', 'gemini-2.0-flash-thinking-exp-01-21', 'gemini-2.0-flash', 'gemini-2.0-flash-lite', 'gemini-1.5-flash-8b'],
    'deepseek': ['deepseek-chat', 'deepseek-reasoner']
};

const availableModels = computed(() => {
    return providerModels[newPrompt.value.provider] || [];
});

const availableModelsEdit = computed(() => {
    return providerModels[editedPrompt.value.provider] || [];
});

const sanitizedChatResponse = computed(() => {
    if (!chatResponse.value) return '';
    try {
        return DOMPurify.sanitize(marked(chatResponse.value));
    } catch (error) {
        console.error('Error in sanitizedChatResponse:', error);
        return '';
    }
});

const chatForm = ref(null);
const chatFormValid = ref(false);

const isValidChatContent = computed(() => {
    return chatPrompt.value && chatPrompt.value.length >= 10 && chatPrompt.value.length <= MAX_PROMPT_LENGTH;
});

const improvements = ref(null);
const improvedPrompt = ref('');
const improvementExplanation = ref('');

// Function to extract JSON object from the response text
const extractJsonObject = (text) => {
    try {
        // First attempt: Look for a complete well-formed JSON object
        const jsonRegex = /(\{[\s\S]*"good_things"\s*:\s*\[\s*(?:\{\s*"aspect"\s*:[^}]*\}\s*,?\s*)*\]\s*,\s*"bad_things"\s*:\s*\[\s*(?:\{\s*"aspect"\s*:[^}]*\}\s*,?\s*)*\]\s*\})/;
        const match = text.match(jsonRegex);

        if (match && match[1]) {
            try {
                const result = JSON.parse(match[1]);
                if (result && Array.isArray(result.good_things) && Array.isArray(result.bad_things)) {
                    return { json: result, jsonString: match[1] };
                }
            } catch (parseErr) {
                console.log("First parsing attempt failed:", parseErr);
                // Continue to next approach
            }
        }

        // Second attempt: Use more relaxed regex and fix common issues
        const relaxedMatch = text.match(/\{[\s\S]*?"good_things"[\s\S]*?"bad_things"[\s\S]*?\}/);
        if (relaxedMatch && relaxedMatch[0]) {
            let jsonStr = relaxedMatch[0];

            // Fix common JSON issues
            jsonStr = jsonStr
                .replace(/,\s*}/g, '}')  // Remove trailing commas in objects
                .replace(/,\s*\]/g, ']') // Remove trailing commas in arrays
                .replace(/(['"])?([a-zA-Z0-9_]+)(['"])?:/g, '"$2":') // Ensure property names are quoted
                .replace(/'/g, '"')      // Replace single quotes with double quotes
                .replace(/\s+/g, ' ')    // Normalize whitespace
                .replace(/,\s*,/g, ','); // Remove duplicate commas

            try {
                const result = JSON.parse(jsonStr);
                if (result && Array.isArray(result.good_things) && Array.isArray(result.bad_things)) {
                    return { json: result, jsonString: jsonStr };
                }
            } catch (parseErr) {
                console.log("Second parsing attempt failed:", parseErr);
            }
        }

        // Final fallback: Manual extraction of good_things and bad_things
        console.log("Attempting manual extraction as last resort");
        const goodThingsMatch = text.match(/"good_things"\s*:\s*\[([\s\S]*?)\]/);
        const badThingsMatch = text.match(/"bad_things"\s*:\s*\[([\s\S]*?)\]/);

        if (goodThingsMatch && badThingsMatch) {
            try {
                // Create a minimal valid JSON structure
                const manualJson = {
                    good_things: [],
                    bad_things: []
                };

                // Try to extract individual items
                const extractItems = (itemsStr) => {
                    const items = [];
                    const itemMatches = itemsStr.match(/\{\s*"aspect"\s*:\s*"([^"]*)"\s*,\s*"description"\s*:\s*"([^"]*)"\s*\}/g);

                    if (itemMatches) {
                        itemMatches.forEach(itemMatch => {
                            const aspectMatch = itemMatch.match(/"aspect"\s*:\s*"([^"]*)"/);
                            const descMatch = itemMatch.match(/"description"\s*:\s*"([^"]*)"/);

                            if (aspectMatch && descMatch) {
                                items.push({
                                    aspect: aspectMatch[1],
                                    description: descMatch[1]
                                });
                            }
                        });
                    }

                    return items;
                };

                manualJson.good_things = extractItems(goodThingsMatch[1]);
                manualJson.bad_things = extractItems(badThingsMatch[1]);

                // If we have at least some items, return the manually constructed JSON
                if (manualJson.good_things.length > 0 || manualJson.bad_things.length > 0) {
                    return { json: manualJson, jsonString: JSON.stringify(manualJson) };
                }
            } catch (e) {
                console.error("Manual extraction failed:", e);
            }
        }

        // Create minimal fallback JSON if everything else fails
        return {
            json: {
                good_things: [{ aspect: "Unknown", description: "Could not parse strengths from response" }],
                bad_things: [{ aspect: "Unknown", description: "Could not parse weaknesses from response" }]
            },
            jsonString: '{}'
        };
    } catch (e) {
        console.error('Error in extractJsonObject:', e);
        // Return minimal valid object as absolute fallback
        return {
            json: {
                good_things: [{ aspect: "Error", description: "Error extracting strengths" }],
                bad_things: [{ aspect: "Error", description: "Error extracting weaknesses" }]
            },
            jsonString: '{}'
        };
    }
};

const improvePrompt = async () => {
    improveDialog.value = true;
    improvingPrompt.value = true;

    // Reset the values before starting the improve process
    improvements.value = null;
    improvedPrompt.value = '';
    improvementExplanation.value = '';

    // We don't touch currentAnswer here anymore
    // This allows the right section to remain visible with its current content

    const language = getLanguageFromLocale(locale.value);

    try {
        const temporaryPrompt = {
            provider: editedPrompt.value.provider,
            model: editedPrompt.value.model,
            temperature: editedPrompt.value.temperature,
            knowledgeId: editedPrompt.value.knowledgeId,
            title: editedPrompt.value.title,
            content: editedPrompt.value.content
        };

        // Make the API call to improve the prompt
        const response = await apiClient.post(`/prompts/${editedPrompt.value.id}/improve?lang=${language}`, temporaryPrompt);

        if (response.status === 200) {
            const improveResponseText = String(response.data);
            console.log("Received improve prompt response - length:", improveResponseText.length);

            // Process the response for improvements section
            try {
                const extracted = extractJsonObject(improveResponseText);

                if (extracted) {
                    improvements.value = {
                        good_things: Array.isArray(extracted.json.good_things) ? extracted.json.good_things : [],
                        bad_things: Array.isArray(extracted.json.bad_things) ? extracted.json.bad_things : []
                    };

                    // Add fallback items if arrays are empty
                    if (improvements.value.good_things.length === 0) {
                        improvements.value.good_things = [{
                            aspect: "Analysis Unavailable",
                            description: "Unable to identify specific strengths in the prompt."
                        }];
                    }

                    if (improvements.value.bad_things.length === 0) {
                        improvements.value.bad_things = [{
                            aspect: "Analysis Unavailable",
                            description: "Unable to identify specific weaknesses in the prompt."
                        }];
                    }

                    // Extract the improved prompt and explanation using the new helper functions
                    improvedPrompt.value = extractImprovedPromptFromText(improveResponseText);
                    improvementExplanation.value = extractExplanationFromText(improveResponseText);

                    console.log("Extracted improved prompt:", improvedPrompt.value.substring(0, 50) + "...");
                    console.log("Extracted explanation:", improvementExplanation.value.substring(0, 50) + "...");
                } else {
                    console.error("Could not extract valid JSON from response");
                    snackbarStore.showMessage('Could not extract strengths and weaknesses from response', 'warning');

                    // Create a fallback improvements object with helpful messages
                    improvements.value = {
                        good_things: [{
                            aspect: "Analysis Unavailable",
                            description: "Unable to identify specific strengths in the prompt."
                        }],
                        bad_things: [{
                            aspect: "Analysis Unavailable",
                            description: "Unable to identify specific weaknesses in the prompt."
                        }]
                    };

                    // Even without JSON, try to extract improved prompt and explanation
                    improvedPrompt.value = extractImprovedPromptFromText(improveResponseText);
                    improvementExplanation.value = extractExplanationFromText(improveResponseText);
                }
            } catch (parseError) {
                console.error("Error parsing JSON analysis:", parseError);
                snackbarStore.showMessage('Error parsing strengths and weaknesses analysis: ' + parseError.message, 'error');

                // Create a minimal improvements object to avoid UI errors
                improvements.value = {
                    good_things: [{
                        aspect: "Analysis Error",
                        description: "Unable to identify specific strengths in the prompt."
                    }],
                    bad_things: [{
                        aspect: "Analysis Error",
                        description: "Unable to identify specific weaknesses in the prompt."
                    }]
                };

                // Try to extract improved prompt and explanation even if JSON parsing failed
                improvedPrompt.value = extractImprovedPromptFromText(improveResponseText);
                improvementExplanation.value = extractExplanationFromText(improveResponseText);
            }
        } else {
            snackbarStore.showMessage('Error improving prompt: ' + (response.data?.message || 'Unknown error'), 'error')
        }
    } catch (error) {
        console.error("Error in improvePrompt:", error);
        snackbarStore.showMessage('Error improving prompt: ' + (error.message || 'Unknown error'), 'error');
    } finally {
        improvingPrompt.value = false;
    }
};

const sanitizedCurrentAnswer = computed(() => {
    if (!currentAnswer.value) return '';
    try {
        return DOMPurify.sanitize(marked(currentAnswer.value));
    } catch (error) {
        console.error('Error in sanitizedCurrentAnswer:', error);
        return '';
    }
});

const sanitizedImprovedPrompt = computed(() => {
    if (!improvedPrompt.value) return '';

    // Remove any section title that might be at the beginning of the improved prompt
    let cleanedPrompt = improvedPrompt.value.replace(/^\s*(?:\d+\.\s+)?(?:Improved|Better|Enhanced)(?:\s+Version(?:\s+of)?)?(?:\s+the)?(?:\s+Prompt)?:?\s*/i, '');

    // Strip markdown formatting characters (**, __, etc.) while preserving the text
    cleanedPrompt = cleanedPrompt
        .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
        .replace(/__(.*?)__/g, '$1')     // Alternative bold
        .replace(/\*(.*?)\*/g, '$1')     // Italic
        .replace(/_(.*?)_/g, '$1')       // Alternative italic
        .replace(/```.*?```/gs, (match) => match.replace(/```/g, '')) // Code blocks
        .replace(/`(.*?)`/g, '$1');      // Inline code

    try {
        // Use sanitize but don't parse markdown to avoid double processing
        return DOMPurify.sanitize(cleanedPrompt);
    } catch (error) {
        console.error('Error in sanitizedImprovedPrompt:', error);
        return cleanedPrompt; // Fallback to plain text if sanitization fails
    }
});

const sanitizedImprovementExplanation = computed(() => {
    if (!improvementExplanation.value) return '';

    // Remove any section title that might be at the beginning of the explanation
    let cleanedExplanation = improvementExplanation.value.replace(/^\s*(?:\d+\.\s+)?(?:Explanation|Rationale|Reason)(?:\s+of)?(?:\s+Improvements)?:?\s*/i, '');

    // Strip markdown formatting characters (**, __, etc.) while preserving the text
    cleanedExplanation = cleanedExplanation
        .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
        .replace(/__(.*?)__/g, '$1')     // Alternative bold
        .replace(/\*(.*?)\*/g, '$1')     // Italic
        .replace(/_(.*?)_/g, '$1')       // Alternative italic
        .replace(/```.*?```/gs, (match) => match.replace(/```/g, '')) // Code blocks
        .replace(/`(.*?)`/g, '$1');      // Inline code

    try {
        // Use sanitize but don't parse markdown to avoid double processing
        return DOMPurify.sanitize(cleanedExplanation);
    } catch (error) {
        console.error('Error in sanitizedImprovementExplanation:', error);
        return cleanedExplanation; // Fallback to plain text if sanitization fails
    }
});

const sanitizedMarkdown = computed(() => {
    return DOMPurify.sanitize(marked(promptResponse.value));
});

const processPrompt = async (item) => {
    try {
        loading.value = true;
        responseDialog.loading = true;

        const language = getLanguageFromLocale(locale.value);

        const response = await apiClient.post(`/prompts/${item.id}/process?lang=${language}`, {
            sources: !!item.knowledgeId
        });

        if (response.status === 200) {
            // The response is either a JSON string (if sources=true) or a plain text string
            const responseData = !!item.knowledgeId ? JSON.parse(response.data) : { response: response.data };
            let formattedResponse = `> #### 🤨\n\n${item.content}\n> #### 🧠\n\n${responseData.response}`;

            if (responseData.sources?.length > 0) {
                formattedResponse += '\n---\n> #### 📚 Sources\n\n' + responseData.sources.map(source => `- ${source}`).join('\n');
            }

            promptResponse.value = formattedResponse;
            responseDialog.value = true;
        } else {
            snackbarStore.showMessage('Error processing prompt: ' + (response.data?.message || 'Unknown error'), 'error')
        }
    } catch (error) {
        snackbarStore.showMessage('Error processing prompt: ' + (error.message || 'Unknown error'), 'error')
    } finally {
        loading.value = false;
    }
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        snackbarStore.showMessage(t('dialog.copiedToClipboard'), 'success')
    } catch (err) {
        snackbarStore.showMessage(t('dialog.copyToClipboardFailed'), 'error')
    }
};

const copyPromptId = async (prompt) => {
    try {
        await navigator.clipboard.writeText(prompt.id);
        snackbarStore.showMessage('Prompt ID copied to clipboard', 'success');
    } catch (err) {
        console.error('Failed to copy prompt ID: ', err);
        snackbarStore.showMessage(t('dialog.copyToClipboardFailed'), 'error');
    }
};

const fetchPrompts = async () => {
    try {
        loading.value = true;
        const response = await apiClient.get('/prompts', {
            headers: {
                'x-iis-user-id': userStore().id,
            },
        });
        prompts.value = Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching prompts:', error);
        prompts.value = []; // Ensure prompts is always an array even on error
        snackbarStore.showError($t('prompt.fetchError'));
    } finally {
        loading.value = false;
    }
};

const fetchKnowledges = async () => {
    try {
        const response = await apiClient.get('/knowledges', {
            headers: {
                'x-iis-user-id': userStore().id,
            },
        });
        knowledges.value = response.data;
    } catch (error) {
        console.error('Error fetching knowledges:', error);
    }
};

const fetchTools = async () => {
    try {
        const response = await apiClient.get('/tools', {
            headers: {
                'x-iis-user-id': userStore().id,
            },
        });
        tools.value = response.data;
    } catch (error) {
        console.error('Error fetching tools:', error);
    }
};

const createPrompt = () => {
    newPrompt.value = {
        userId: "1",
        title: '',
        content: '',
        search: '',
        provider: '',
        model: '',
        temperature: defaultTemperature,
        knowledges: null,
        tools: null
    };
    dialog.value = true;
};

const editPrompt = (item) => {
    editedPrompt.value = {
        id: item.id,
        userId: item.userId,
        title: item.title,
        content: item.content,
        search: item.search,
        provider: item.provider,
        model: item.model,
        temperature: item.temperature,
        knowledges: item.knowledges?.map(k => k.knowledgeId) || [],
        tools: item.tools?.map(t => t.toolId) || []
    };
    editDialog.value = true;
};

const deletePrompt = async (prompt) => {
    try {
        loading.value = true;
        await apiClient.delete(`/prompts/${prompt.id}`);
        await fetchPrompts();
    } catch (error) {
        console.error('Error deleting prompt:', error);
    } finally {
        loading.value = false;
    }
};

const savePrompt = async () => {
    try {
        loading.value = true;
        const { knowledges, tools, ...promptData } = newPrompt.value;

        // Ensure we're sending an array of knowledge IDs
        const knowledgeIds = Array.isArray(knowledges) ? knowledges : [];

        // Ensure we're sending an array of tool IDs
        const toolIds = Array.isArray(tools) ? tools : [];

        await apiClient.post('/prompts', {
            ...promptData,
            knowledgeIds, // Backend expects knowledgeIds
            toolIds // Backend expects toolIds
        });
        await fetchPrompts();
        createPromptDialog.value = false;
        newPrompt.value = {
            userId: "1",
            title: '',
            content: '',
            provider: '',
            model: '',
            temperature: defaultTemperature,
            knowledges: [],
            tools: []
        };
    } catch (error) {
        console.error('Error creating prompt:', error);
    } finally {
        loading.value = false;
    }
};

const updatePrompt = async () => {
    try {
        loading.value = true;
        const { knowledges, tools, ...promptData } = editedPrompt.value;

        // Ensure we're sending an array of knowledge IDs
        const knowledgeIds = Array.isArray(knowledges) ? knowledges : [];

        // Ensure we're sending an array of tool IDs
        const toolIds = Array.isArray(tools) ? tools : [];

        console.log("update prompts with ", knowledgeIds, toolIds);

        await apiClient.put(`/prompts/${editedPrompt.value.id}`, {
            ...promptData,
            knowledges: { create: knowledgeIds.map(k => ({ knowledgeId: k }))},
            tools: { create: toolIds.map(t => ({ toolId: t }))}
        });
        await fetchPrompts();
        editDialog.value = false;
    } catch (error) {
        console.error('Error updating prompt:', error);
    } finally {
        loading.value = false;
    }
};

const processChatPrompt = async () => {
    processingChat.value = true;
    try {
    if (chatOptions.value.knowledges?.length > 0) {
        const temporaryPrompt = {
            provider: chatOptions.value.provider,
            model: chatOptions.value.model,
            temperature: chatOptions.value.temperature,
            knowledges: chatOptions.value.knowledges.map(id => ({ knowledgeId: id })),
            tools: chatOptions.value.tools.map(id => (id)),
            content: chatPrompt.value,
            search: chatOptions.value.search,
            userId: "1"
        };
        const processPromptRequest = {
            prompt: temporaryPrompt
        }

        console.log("process chat with ", temporaryPrompt);
        const language = getLanguageFromLocale(locale.value);

        const response = await apiClient.post(`/prompts/00000000-0000-0000-0000-000000000000/process?lang=${language}`, processPromptRequest);
        if (response.status === 200) {
            // Handle the response which may be a JSON string or an object
            try {
                // Check if the response is already an object or a JSON string
                const responseData = typeof response.data === 'string'
                    ? JSON.parse(response.data)
                    : response.data;

                // Extract the response text
                chatResponse.value = responseData.response;

                console.log('Parsed chat response:', chatResponse.value);
            } catch (parseError) {
                console.error('Error parsing response:', parseError);
                // Fallback to using the raw response if parsing fails
                chatResponse.value = typeof response.data === 'string'
                    ? response.data
                    : JSON.stringify(response.data);
            }
        } else {
            snackbarStore.showMessage('Error processing the chat: ' + (response.data?.message || 'Unknown error'), 'error');
        }
    } else {
            const response = await apiClient.post('/chat', {
                request: chatPrompt.value,
                provider: chatOptions.value.provider,
                model: chatOptions.value.model,
                temperature: chatOptions.value.temperature,
                tools: chatOptions.value.tools.map(id => (id))
            });
            if (response.status === 200) {
                chatResponse.value = response.data.response;
            } else {
                snackbarStore.showMessage('Error processing chat: ' + (response.data?.message || 'Unknown error'), 'error');
            }
        }
    } catch (error) {
            snackbarStore.showMessage('Error processing chat: ' + (error.message || 'Unknown error'), 'error');
    } finally {
            processingChat.value = false;
    }
};

const validateAndProcessChat = async () => {
    const { valid } = await chatForm.value.validate();

    if (valid) {
        processChatPrompt();
    }
};

const form = ref(null);
const formValid = ref(false);

// Load saved filter from local storage on component creation
onMounted(() => {
    const savedFilter = localStorage.getItem('prompt-search-filter');
    if (savedFilter) {
        searchFilter.value = savedFilter;
    }
});

const applyFilter = () => {
    // Save filter to local storage
    localStorage.setItem('prompt-search-filter', searchFilter.value);

    // If we're using the API for filtering
    // fetchPrompts();

    // For client-side filtering, we don't need to fetch again
};

const validateAndSave = async () => {
    const { valid } = await form.value.validate();

    if (valid) {
        savePrompt();
    }
};

const getLanguageFromLocale = (localeValue) => {
    switch (localeValue) {
        case 'fr':
            return 'French';
        default:
            return 'English';
    }
};

const useImprovedPrompt = () => {
    if (improvedPrompt.value) {
        // Update the edited prompt content with the improved version
        editedPrompt.value.content = improvedPrompt.value;
        snackbarStore.showMessage('Prompt updated with improved version', 'success');
    } else {
        snackbarStore.showMessage('No improved prompt available', 'warning');
    }
};

const processingImproved = ref(false);

const processWithImprovedPrompt = async () => {
    if (!improvedPrompt.value) {
        snackbarStore.showMessage('No improved prompt available', 'warning');
        return;
    }

    try {
        processingImproved.value = true;
        const language = getLanguageFromLocale(locale.value);

        // Create a temporary prompt with the improved content
        const tempPrompt = {
            ...editedPrompt.value,
            content: improvedPrompt.value
        };

        // Process the improved prompt
        const response = await apiClient.post(
            `/prompts/${editedPrompt.value.id}/process?lang=${language}`,
            tempPrompt
        );

        if (response.status === 200) {
            currentAnswer.value = String(response.data);
        } else {
            snackbarStore.showMessage('Error processing improved prompt', 'error');
        }
    } catch (error) {
        console.error("Error processing with improved prompt:", error);
        snackbarStore.showMessage('Error: ' + (error.message || 'Unknown error'), 'error');
    } finally {
        processingImproved.value = false;
    }
};

const formattedStrengths = computed(() => {
    return improvements.value?.good_things?.map(item => `${item.aspect}: ${item.description}`).join('\n') || '';
});

/**
 * Helper function to clean extracted text by removing markdown and quotes
 * @param {string} text - Text to clean
 * @returns {string} - Text with formatting removed
 */
const cleanTextFormatting = (text) => {
    return text
        .replace(/^["']|["']$/g, '') // Remove quotes at start/end if present
        .replace(/\*\*/g, '')        // Remove bold formatting
        .replace(/\*/g, '')          // Remove italic formatting
        .replace(/`/g, '');          // Remove code formatting
};

/**
 * Generic helper function to extract a section from text
 * @param {string} text - Full text to extract from
 * @param {RegExp[]} patterns - Regex patterns to try matching
 * @param {RegExp} lineStartPattern - Pattern to match for line-by-line search
 * @returns {string} - Extracted and cleaned text or empty string
 */
const extractSection = (text, patterns, lineStartPattern) => {
    // Try pattern matching first
    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match && match[1] && match[1].trim().length > 0) {
            return cleanTextFormatting(match[1].trim());
        }
    }

    // Fallback to line-by-line search
    const lines = text.split('\n');
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.match(lineStartPattern)) {
            return cleanTextFormatting(lines.slice(i + 1).join('\n').trim());
        }
    }

    return ""; // Return empty string if no pattern matches
};

const extractImprovedPromptFromText = (text) => {
    const patterns = [
        // Pattern to match "2. Improved Version of the Prompt:" and capture everything until the next numbered section
        /2\.\s+(?:Improved|Better|Enhanced)(?:\s+Version(?:\s+of)?)?(?:\s+the)?(?:\s+Prompt)?:?\s*([\s\S]*?)(?=\d+\.\s+|$)/i,

        // Alternative pattern for different formats
        /(?:Improved|Better|Enhanced)(?:\s+Version(?:\s+of)?)?(?:\s+the)?(?:\s+Prompt)?:?\s*([\s\S]*?)(?=\d+\.\s+|$)/i,

        // Try to match any quoted text that might be the prompt
        /"([^"]{20,})"/,
        /'([^']{20,})'/,

        // For section formats with just numbers
        /2\.\s+([\s\S]*?)(?=3\.\s+|$)/i,

        // For numbered sections only
        /^2[\.\)]\s+([\s\S]*?)(?=^3[\.\)]|$)/im
    ];

    const lineStartPattern = /^2\.\s+|^Improved\s+|^Better\s+|^Enhanced\s+/i;

    return extractSection(text, patterns, lineStartPattern);
};

const extractExplanationFromText = (text) => {
    const patterns = [
        // Pattern to match "3. Explanation of Improvements:" and capture everything until the end
        /3\.\s+(?:Explanation|Rationale|Reason)(?:\s+of)?(?:\s+Improvements)?:?\s*([\s\S]*?)(?=\d+\.\s+|$)/i,

        // Alternative pattern for different formats
        /(?:Explanation|Rationale|Reason)(?:\s+of)?(?:\s+Improvements)?:?\s*([\s\S]*?)(?=\d+\.\s+|$)/i,

        // For section formats with just numbers
        /3\.\s+([\s\S]*?)(?=4\.\s+|$)/i,

        // For numbered sections only
        /^3[\.\)]\s+([\s\S]*?)(?=^4[\.\)]|$)/im
    ];

    const lineStartPattern = /^3\.\s+|^Explanation\s+|^Rationale\s+|^Reason\s+/i;

    return extractSection(text, patterns, lineStartPattern);
};

const processingCurrentPrompt = ref(false);

const processCurrentPrompt = async () => {
    processingCurrentPrompt.value = true;

    try {
        const language = getLanguageFromLocale(locale.value);

        const temporaryPrompt = {
            provider: editedPrompt.value.provider,
            model: editedPrompt.value.model,
            temperature: editedPrompt.value.temperature,
            knowledgeId: editedPrompt.value.knowledgeId,
            title: editedPrompt.value.title,
            content: editedPrompt.value.content
        };

        const response = await apiClient.post(`/prompts/${editedPrompt.value.id}/process?lang=${language}`, temporaryPrompt);

        if (response.status === 200) {
            currentAnswer.value = String(response.data);
        } else {
            snackbarStore.showMessage('Error processing prompt: ' + (response.data?.message || 'Unknown error'), 'error');
        }
    } catch (error) {
        console.error("Error processing current prompt:", error);
        snackbarStore.showMessage('Error: ' + (error.message || 'Unknown error'), 'error');
    } finally {
        processingCurrentPrompt.value = false;
    }
};

onMounted(() => {
    fetchPrompts();
    fetchKnowledges();
    fetchTools();
});
</script>

<style scoped>
.response-textarea-light {
    background-color: #beaa7d;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 16px;
}

.response-textarea-dark {
    background-color: #4a4a4a;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 16px;
}

.bordered-div {
    border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 4px;
}

.text-green {
    color: green;
}

.text-red {
    color: red;
}

.resizable {
    resize: both;
    overflow: auto;
    min-width: 400px;
    min-height: 400px;
}

.uppercase-headers :deep(th) {
    text-transform: uppercase !important;
}

.chat-message {
    white-space: pre-wrap;
}
</style>
